/* eslint-disable react/require-default-props */
import * as React from "react";
import SigPad, { Options, PointGroup } from "signature_pad";
import { debounce } from "throttle-debounce";
import { css } from "@emotion/css";
// eslint-disable-next-line import/no-unresolved
import { BasicPoint } from "signature_pad/dist/types/point";

interface PfSignaturePadProps {
  width?: number;
  height?: number;
  redrawOnResize?: boolean;
  debounceInterval?: number;
  options?: Options;
  aspect?: number;
}

export const PfSignaturePad = React.forwardRef(
  (
    {
      width,
      height,
      options,
      redrawOnResize = false,
      debounceInterval = 150,
      aspect = 0.3
    }: PfSignaturePadProps,
    forwardedRef
  ) => {
    const [canvasWidth, setCanvasWidth] = React.useState(0);
    const [canvasHeight, setCanvasHeight] = React.useState(0);

    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const signaturePadInstance = React.useRef<SigPad | null>(null);

    const scaleCanvas = React.useCallback(() => {
      if (canvasRef.current) {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const actualWidth = (width || canvasRef.current.offsetWidth) * ratio;
        const actualHeight = aspect
          ? aspect * actualWidth
          : (height || canvasRef.current.offsetHeight) * ratio;

        // Avoid needlessly setting height/width if dimensions haven't changed
        if (actualWidth === canvasWidth && actualHeight === canvasHeight)
          return;

        let data;
        const processedData: PointGroup[] = [];
        if (redrawOnResize && signaturePadInstance.current) {
          data = signaturePadInstance.current.toData();
        }

        const dx = actualWidth / canvasRef.current.width;
        const dy = actualHeight / canvasRef.current.height;

        if (data && data.length !== 0) {
          data.forEach(linepoints => {
            linepoints.points = linepoints.points.map(point => {
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              return {
                x: point.x * dx,
                y: point.y * dy,
                time: point.time
              } as BasicPoint;
            });
            processedData.push(linepoints);
          });
        }

        canvasRef.current.width = actualWidth;
        canvasRef.current.height = actualHeight;

        setCanvasWidth(actualWidth);
        setCanvasHeight(actualHeight);

        const ctx = canvasRef.current.getContext("2d");
        if (ctx) {
          ctx.scale(ratio, ratio);
        }
        if (signaturePadInstance.current) {
          if (redrawOnResize) {
            if (processedData.length !== 0) {
              signaturePadInstance.current.fromData(processedData);
            }
          } else {
            signaturePadInstance.current.clear();
          }
        }
      }
    }, [
      aspect,
      canvasHeight,
      canvasWidth,
      width,
      height,
      redrawOnResize,
      signaturePadInstance
    ]);

    const callResizeHandler = React.useCallback(
      debounce(debounceInterval, scaleCanvas),
      []
    );

    // new a instance of signaure_pad after component mounted.
    React.useEffect(() => {
      if (canvasRef && canvasRef.current)
        signaturePadInstance.current = new SigPad(canvasRef.current, options);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // assign the instance to the forwarded ref to expose the instance.
    React.useEffect(() => {
      if (forwardedRef)
        (forwardedRef as React.MutableRefObject<SigPad | null>).current =
          signaturePadInstance.current;
    }, [forwardedRef]);

    // useLayoutEffect will excute synchronously before the broswer paint. avoiding werid visual effect on component load
    React.useLayoutEffect(() => {
      scaleCanvas();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
      if ((!width || !height) && signaturePadInstance.current) {
        window.addEventListener("resize", callResizeHandler);
        signaturePadInstance.current.on();
      }
      return () => {
        if ((!width || !height) && signaturePadInstance.current) {
          window.removeEventListener("resize", callResizeHandler);
          signaturePadInstance.current.off();
        }
      };
    }, [width, height, debounceInterval, scaleCanvas, callResizeHandler]);

    return (
      <div
        className={css`
          border: 1px dashed grey;
          margin-bottom: 5px;
        `}
      >
        <canvas
          style={{ width: "100%" }}
          ref={canvasRef}
          data-testid="signCanvas"
        />
      </div>
    );
  }
);

PfSignaturePad.displayName = "PfSignaturePad";
